import { EditorView } from '@codemirror/view';

export function bindScrollSync(view: EditorView, preview: HTMLElement): () => void {
  let syncing = false;
  let raf = 0;

  const lineToElement = (): Map<number, HTMLElement> => {
    const m = new Map<number, HTMLElement>();
    preview.querySelectorAll<HTMLElement>('[data-source-line]').forEach((el) => {
      const n = parseInt(el.dataset.sourceLine || '0', 10);
      if (n) m.set(n, el);
    });
    return m;
  };

  const editorToPreview = () => {
    if (syncing) return;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      syncing = true;
      try {
        const scrollDOM = view.scrollDOM;
        const top = scrollDOM.scrollTop + 8;
        const block = view.lineBlockAtHeight(top - view.documentTop);
        const line = view.state.doc.lineAt(block.from).number;

        const map = lineToElement();
        const lines = Array.from(map.keys()).sort((a, b) => a - b);
        let lo: number | undefined, hi: number | undefined;
        for (const l of lines) {
          if (l <= line) lo = l;
          if (l >= line && hi === undefined) hi = l;
        }
        if (lo === undefined) return;
        const loEl = map.get(lo)!;
        const hiEl = hi !== undefined ? map.get(hi)! : loEl;
        const ratio = hi && hi !== lo ? (line - lo) / (hi - lo) : 0;
        const targetTop = loEl.offsetTop + (hiEl.offsetTop - loEl.offsetTop) * ratio;
        preview.scrollTop = targetTop - 8;
      } finally {
        requestAnimationFrame(() => {
          syncing = false;
        });
      }
    });
  };

  const previewToEditor = () => {
    if (syncing) return;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      syncing = true;
      try {
        const top = preview.scrollTop + 8;
        const map = lineToElement();
        const entries = Array.from(map.entries()).sort((a, b) => a[1].offsetTop - b[1].offsetTop);
        let lo: [number, HTMLElement] | undefined;
        let hi: [number, HTMLElement] | undefined;
        for (const e of entries) {
          if (e[1].offsetTop <= top) lo = e;
          if (e[1].offsetTop >= top && !hi) hi = e;
        }
        if (!lo) return;
        const ratio = hi && hi[1].offsetTop !== lo[1].offsetTop
          ? (top - lo[1].offsetTop) / (hi[1].offsetTop - lo[1].offsetTop)
          : 0;
        const targetLine = Math.round(lo[0] + ((hi?.[0] ?? lo[0]) - lo[0]) * ratio);
        const docLines = view.state.doc.lines;
        const clamped = Math.max(1, Math.min(targetLine, docLines));
        const pos = view.state.doc.line(clamped).from;
        view.dispatch({ effects: EditorView.scrollIntoView(pos, { y: 'start', yMargin: 8 }) });
      } finally {
        requestAnimationFrame(() => {
          syncing = false;
        });
      }
    });
  };

  view.scrollDOM.addEventListener('scroll', editorToPreview, { passive: true });
  preview.addEventListener('scroll', previewToEditor, { passive: true });

  return () => {
    view.scrollDOM.removeEventListener('scroll', editorToPreview);
    preview.removeEventListener('scroll', previewToEditor);
    cancelAnimationFrame(raf);
  };
}
